import { useCallback, useState } from 'react';

import type { Question, QuestionnaireAnswers } from '@shared';
import {
  getOtherTextField,
  supportsEditableOther,
} from '@/features/advisor-onboarding/questionnaire/lib/editable-other';

interface UseMultiSelectQuestionOptions {
  question: Question;
  initialValue: string[];
  initialOtherText: string;
  onSubmit: (patch: Partial<QuestionnaireAnswers>) => Promise<void> | void;
}

export interface MultiSelectQuestionState {
  value: string[];
  setValue: (next: string[]) => void;
  otherText: string;
  setOtherText: (next: string) => void;
  hasEditableOther: boolean;
  isValid: boolean;
  isEmpty: boolean;
  submit: () => Promise<void>;
}

export function useMultiSelectQuestion({
  question,
  initialValue,
  initialOtherText,
  onSubmit,
}: UseMultiSelectQuestionOptions): MultiSelectQuestionState {
  const [value, setValue] = useState<string[]>(initialValue);
  const [otherText, setOtherText] = useState<string>(initialOtherText);

  const hasEditableOther = supportsEditableOther(question.id);
  const otherKey = getOtherTextField(question.id);
  const minSelections = (question as { minSelections?: number }).minSelections ?? 0;

  const otherSelectedNeedsText = hasEditableOther && value.includes('other') && !otherText.trim();
  const isValid = value.length >= minSelections && !otherSelectedNeedsText;

  const submit = useCallback(async () => {
    if (!isValid) return;

    const patch: Partial<QuestionnaireAnswers> = {
      [question.id]: value,
    } as Partial<QuestionnaireAnswers>;

    if (hasEditableOther && otherKey) {
      (patch as Record<string, unknown>)[otherKey] = value.includes('other')
        ? otherText.trim()
        : undefined;
    }

    await onSubmit(patch);
  }, [isValid, value, question.id, hasEditableOther, otherKey, otherText, onSubmit]);

  return {
    value,
    setValue,
    otherText,
    setOtherText,
    hasEditableOther,
    isValid,
    isEmpty: value.length === 0,
    submit,
  };
}
